import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../../shared/api/client";
import type { ClassState, PresetItem } from "../../shared/api/types";
import { DESIGN_CUTE_DB_PATH, image } from "../../shared/assets/manifest";
import { can, hasRole } from "../../shared/auth/capabilities";
import { errorMessage, useToast } from "../../shared/ui/ToastProvider";

export const CATEGORY_META: Record<string, { label: string; award: string; icon: string; image: string }> = {
  sport: { label: "健体", award: "健体小标兵", icon: image.sport, image: image.sport },
  hygiene: { label: "卫生", award: "卫生小标兵", icon: image.hygiene, image: image.hygiene },
  study: { label: "学习", award: "学习小标兵", icon: image.study, image: image.study },
  discipline: { label: "纪律", award: "纪律小标兵", icon: image.discipline, image: image.discipline },
  manners: { label: "礼仪", award: "礼仪小标兵", icon: image.manners, image: image.manners },
  manual: { label: "自定义", award: "综合表现", icon: image.manual, image: image.manual }
};

export const AWARD_ORDER = ["excellent_group", "sport", "hygiene", "study", "discipline", "manners"] as const;

export function awardLabel(row: { awardType: string; awardLabel?: string }) {
  if (row.awardLabel) return row.awardLabel;
  if (row.awardType === "excellent_group") return "优秀小组";
  return CATEGORY_META[row.awardType]?.award || row.awardType;
}

export function monthLabel(key: string) {
  const [year, month] = key.split("-").map(Number);
  if (!year || !month) return key;
  return `${year} 年 ${month} 月`;
}

export function monthlyGroupScores(records: Array<{ groupId?: number | null; groupName?: string; amount?: number; studentId?: number | null }>) {
  const byGroup = new Map<string | number, { id: number | null; name: string; total: number; recordCount: number; studentIds: Set<number> }>();
  for (const record of records) {
    const groupKey = record.groupId ?? record.groupName;
    if (groupKey == null || groupKey === "") continue;
    const existing = byGroup.get(groupKey) || {
      id: record.groupId ?? null,
      name: record.groupName || "未命名小组",
      total: 0,
      recordCount: 0,
      studentIds: new Set<number>()
    };
    existing.total += Number(record.amount || 0);
    existing.recordCount += 1;
    if (record.studentId) existing.studentIds.add(record.studentId);
    byGroup.set(groupKey, existing);
  }
  return [...byGroup.values()]
    .map((group) => ({ ...group, memberCount: group.studentIds.size }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, "zh-CN"));
}

export function monthlyStudentScores(
  records: Array<{ studentId?: number | null; studentName?: string; amount?: number; category?: string }>,
  category = ""
) {
  const byStudent = new Map<string | number, { id: number | null; name: string; score: number; recordCount: number }>();
  for (const record of records) {
    if (category && record.category !== category) continue;
    const studentKey = record.studentId ?? record.studentName;
    if (studentKey == null || studentKey === "") continue;
    const existing = byStudent.get(studentKey) || {
      id: record.studentId ?? null,
      name: record.studentName || "已删除学生",
      score: 0,
      recordCount: 0
    };
    existing.score += Number(record.amount || 0);
    existing.recordCount += 1;
    byStudent.set(studentKey, existing);
  }
  return [...byStudent.values()]
    .filter((item) => item.score !== 0)
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name, "zh-CN"));
}

export function watchStudents(
  students: Array<{ id: number; points: number }>,
  history: Array<{ studentId?: number | null; amount?: number; time?: string; voided?: boolean }>
) {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return students.filter((student) => {
    if (student.points < 0) return true;
    const hasRecentPositive = history.some((record) => {
      if (record.voided || Number(record.studentId) !== Number(student.id) || Number(record.amount || 0) <= 0) return false;
      const time = record.time ? new Date(record.time) : null;
      return Boolean(time && !Number.isNaN(time.getTime()) && time.getTime() >= weekAgo);
    });
    return !hasRecentPositive && history.length > 0;
  });
}

export function categoryIconSrc(category = "manual") {
  return CATEGORY_META[category]?.image || CATEGORY_META.manual.image;
}

/** Scene illustrations for points preset cards (large art). Keep small icons via categoryIconSrc. */
const PRESET_ART_BY_CATEGORY: Record<string, { pos: string[]; neg: string[] }> = {
  sport: {
    pos: ["design-cute/preset-scene-exercise.png"],
    neg: ["design-cute/preset-scene-distracted.png"]
  },
  hygiene: {
    pos: ["design-cute/preset-scene-clean.png"],
    neg: ["design-cute/preset-scene-messy.png"]
  },
  study: {
    pos: [
      "design-cute/preset-scene-answer.png",
      "design-cute/preset-scene-idea.png",
      "design-cute/preset-scene-read.png",
      "design-cute/preset-scene-listen.png"
    ],
    neg: ["design-cute/preset-scene-distracted.png"]
  },
  discipline: {
    pos: ["design-cute/preset-scene-orderly.png"],
    neg: ["design-cute/preset-scene-talk.png", "design-cute/preset-scene-messy.png"]
  },
  manners: {
    pos: ["design-cute/preset-scene-help.png", "design-cute/preset-scene-polite.png"],
    neg: ["design-cute/preset-scene-talk.png"]
  },
  manual: {
    pos: ["design-cute/preset-scene-idea.png"],
    neg: ["design-cute/preset-scene-distracted.png"]
  }
};

const PRESET_SCENE_BY_LABEL: Array<{ match: RegExp; scene: string }> = [
  { match: /积极|发言|回答/, scene: "design-cute/preset-scene-answer.png" },
  { match: /听讲|专心|早读|朗读/, scene: "design-cute/preset-scene-listen.png" },
  { match: /作业|书写|阅读|晨读/, scene: "design-cute/preset-scene-read.png" },
  { match: /思路|进步|创意|想法/, scene: "design-cute/preset-scene-idea.png" },
  { match: /课间操|体育|眼保健|运动/, scene: "design-cute/preset-scene-exercise.png" },
  { match: /打扫|整洁|卫生(?!扣)/, scene: "design-cute/preset-scene-clean.png" },
  { match: /帮助|合作|小组合作/, scene: "design-cute/preset-scene-help.png" },
  { match: /礼貌|尊敬|着装/, scene: "design-cute/preset-scene-polite.png" },
  { match: /纪律|排队|坐姿|站队|队列/, scene: "design-cute/preset-scene-orderly.png" },
  { match: /走神|未完成|未交/, scene: "design-cute/preset-scene-distracted.png" },
  { match: /讲话|打断|说话/, scene: "design-cute/preset-scene-talk.png" },
  { match: /乱扔|杂乱|迟到|卫生扣/, scene: "design-cute/preset-scene-messy.png" }
];

function toAssetUrl(path: string) {
  if (path.startsWith("/assets/images/")) return path;
  if (path.startsWith("assets/images/")) return `/${path}`;
  if (path.startsWith("design-cute/")) return `/assets/images/${path}`;
  return path;
}

export function presetIconSrc(preset: Pick<PresetItem, "icon" | "category">) {
  const icon = typeof preset.icon === "string" ? preset.icon.trim() : "";
  if (!icon) return categoryIconSrc(preset.category);
  // Scene illustrations are for large cards only — never shrink them to 24px list icons.
  if (icon.includes("preset-scene-")) return categoryIconSrc(preset.category);
  if (icon.startsWith("/assets/images/")) return icon;
  if (
    (icon.startsWith("design-cute/") || icon.startsWith(DESIGN_CUTE_DB_PATH)) &&
    (icon.endsWith(".png") || icon.endsWith(".svg")) &&
    !icon.includes("..")
  ) {
    return icon.startsWith("assets/") ? `/${icon}` : `/assets/images/${icon}`;
  }
  // Emoji / short labels fall back to category art.
  if (icon.length <= 4) return categoryIconSrc(preset.category);
  return categoryIconSrc(preset.category);
}

/** Large card art for the points page — prefer scene illustrations over tiny category icons. */
export function presetArtSrc(
  preset: Pick<PresetItem, "icon" | "category" | "points" | "label" | "id">,
  index = 0
) {
  const icon = typeof preset.icon === "string" ? preset.icon.trim() : "";
  if (icon.includes("preset-scene-") && (icon.endsWith(".png") || icon.endsWith(".svg")) && !icon.includes("..")) {
    return toAssetUrl(icon);
  }

  const label = String(preset.label || "");
  const byLabel = PRESET_SCENE_BY_LABEL.find((item) => item.match.test(label));
  if (byLabel) return toAssetUrl(byLabel.scene);

  const pool = PRESET_ART_BY_CATEGORY[preset.category] || PRESET_ART_BY_CATEGORY.manual;
  const list = (Number(preset.points) < 0 ? pool.neg : pool.pos) || pool.pos;
  const arts = list.length ? list : pool.pos;
  const seed = Math.abs(Math.trunc(Number(preset.id?.replace(/\D/g, "") || index) || index));
  return toAssetUrl(arts[seed % arts.length]);
}

export function invalidateState(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["state"] }),
    queryClient.invalidateQueries({ queryKey: ["monthly-awards"] }),
    queryClient.invalidateQueries({ queryKey: ["monthly-history"] }),
    queryClient.invalidateQueries({ queryKey: ["history"] }),
    queryClient.invalidateQueries({ queryKey: ["insights"] }),
    queryClient.invalidateQueries({ queryKey: ["overview-stats"] }),
    queryClient.invalidateQueries({ queryKey: ["counseling"] }),
    queryClient.invalidateQueries({ queryKey: ["exams"] }),
    queryClient.invalidateQueries({ queryKey: ["academic"] })
  ]);
}

export function signed(amount: number) {
  return amount > 0 ? `+${amount}` : `${amount}`;
}

export function useApplyStudentPoints() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { studentIds: number[]; amount: number; reason?: string; category?: string }) =>
      api<ClassState>("/api/points/students", {
        method: "POST",
        body: JSON.stringify({
          studentIds: vars.studentIds,
          amount: vars.amount,
          reason: vars.reason || "",
          category: vars.category || "manual"
        })
      }),
    onSuccess: (payload, vars) => {
      invalidateState(queryClient);
      toast(`已记分：${signed(vars.amount)}，共 ${vars.studentIds.length} 人`, vars.amount > 0 ? "success" : "warning");
      return payload;
    },
    onError: (error) => toast(errorMessage(error, "记分失败"), "error")
  });
}

export function useApplyGroupPoints() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { groupId: number; amount: number; reason?: string; category?: string }) =>
      api<ClassState>("/api/points/group", {
        method: "POST",
        body: JSON.stringify({
          groupId: vars.groupId,
          amount: vars.amount,
          reason: vars.reason || "",
          category: vars.category || "manual"
        })
      }),
    onSuccess: (payload, vars) => {
      invalidateState(queryClient);
      const count = Number(payload.count ?? 0);
      toast(`小组记分完成：${count} 人 ${signed(vars.amount)}`, vars.amount > 0 ? "success" : "warning");
      return payload;
    },
    onError: (error) => toast(errorMessage(error, "记分失败"), "error")
  });
}

export function canWrite(state: ClassState) {
  return can(state.currentTeacher, "roster.write");
}

export function canWriteGrowth(state: ClassState) {
  return can(state.currentTeacher, "growth.write");
}

export function isOwner(state: ClassState) {
  return hasRole(state.currentTeacher, "owner");
}

export function canManageOrg(state: ClassState) {
  return can(state.currentTeacher, "classes.manage");
}

export function roleLabel(role: string | undefined) {
  switch (role) {
    case "owner":
      return "管理员";
    case "org_admin":
      return "机构管理员";
    case "teacher":
      return "教师";
    case "viewer":
      return "只读";
    default:
      return role || "未知";
  }
}

export function formatBytes(value: number | undefined) {
  const size = Number(value || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTime(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", { hour12: false });
}

export function buildImportPreviewMessage(preview: {
  incoming?: { className?: string; students?: number; groups?: number; history?: number; presets?: number };
  current?: { className?: string; students?: number; groups?: number; history?: number };
  warnings?: string[];
} = {}) {
  const incoming = preview.incoming || {};
  const current = preview.current || {};
  const warnings = Array.isArray(preview.warnings) ? preview.warnings : [];
  const count = (value: unknown) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  return [
    current.className || incoming.className
      ? `当前班级「${current.className || "未知班级"}」将导入为「${incoming.className || "未命名班级"}」。`
      : "",
    `将导入 ${count(incoming.students)} 名学生、${count(incoming.groups)} 个小组、${count(incoming.history)} 条记录。`,
    incoming.presets == null ? "" : `导入文件包含 ${count(incoming.presets)} 个旧版预设项；当前班级导入不会覆盖机构预设。`,
    `当前班级已有 ${count(current.students)} 名学生、${count(current.groups)} 个小组、${count(current.history)} 条记录，会被覆盖。`,
    warnings.length ? `提示：${warnings.join("；")}` : "",
    "确认继续？"
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildBackupPreviewMessage(
  backup: { name?: string } = {},
  preview: {
    counts?: Record<string, number>;
    classes?: Array<{ name?: string }>;
    firstClass?: { name?: string };
  } = {}
) {
  const counts = preview.counts || {};
  const classes = Array.isArray(preview.classes) ? preview.classes : [];
  const firstClass = preview.firstClass?.name || "未知班级";
  const classNames = classes
    .slice(0, 6)
    .map((item) => item.name)
    .filter(Boolean)
    .join("、");
  return [
    `确定恢复备份「${backup.name || "未命名备份"}」吗？`,
    "",
    `备份班级：${classNames || firstClass}${classes.length > 6 ? ` 等 ${classes.length} 个` : ""}`,
    `学生：${counts.students ?? "-"}`,
    `小组：${counts.groups ?? "-"}`,
    `积分记录：${counts.point_history ?? "-"}`,
    `教师账号：${counts.teachers ?? "-"}`,
    "",
    "当前数据会被覆盖，系统会先自动保存一份恢复前备份。"
  ].join("\n");
}

export function groupScores(state: ClassState) {
  return state.groups
    .map((group) => {
      const members = state.students.filter((student) => Number(student.groupId) === Number(group.id));
      const total = members.reduce((sum, student) => sum + Number(student.points || 0), 0);
      return {
        ...group,
        total,
        count: members.length,
        memberCount: members.length,
        avg: members.length ? Math.round(total / members.length) : 0
      };
    })
    .sort((a, b) => b.total - a.total);
}

export function groupNameFor(state: ClassState, groupId: number | null | undefined) {
  return state.groups.find((group) => Number(group.id) === Number(groupId))?.name || "未分组";
}
